// @dynamic
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import * as pluralize_ from 'pluralize';
import { find, each } from 'lodash';
import { RegisteredResource } from '../interfaces/registered-resource';
import { removeSlashes } from '../utils/remove-slashes';
import { Observable } from 'rxjs/Observable';
import { SDAngularJsonAPIConfig } from '../interfaces/config';
import { PaginationStrategy } from '../interfaces/pagination-strategy';
import { PaginationOptions } from '../interfaces/pagination-options';
import { JSONAPIError, JSONAPIErrorBag, JSONAPIErrorResponse } from '../utils/sd-angular-jsonapi-error';
import { Collection } from '../utils/sd-angular-jsonapi-collection';
import { JSON_API_ERROR_MESSAGES } from '../utils/error-messages';
import { JSONAPIResource } from '../utils/sd-angular-jsonapi-resource';

const pluralize = pluralize_;

@Injectable()
export class SDAngularJsonAPIRequestService {

	private config: SDAngularJsonAPIConfig = {
		baseUrl: '',
		version: '',
		paginationStrategy: null,
		paginationOptions: null
	};

	private resources: RegisteredResource[] = [];

	constructor (private http: HttpClient) {}

	configure (config: SDAngularJsonAPIConfig) {
		Object.assign(this.config, config);
		return this;
	}

	setBaseUrl (url: string): SDAngularJsonAPIRequestService {
		this.config.baseUrl = url;
		return this;
	}

	setVersion (version: string): SDAngularJsonAPIRequestService {
		this.config.version = version;
		return this;
	}

	setPaginationStrategy (strategy: PaginationStrategy): SDAngularJsonAPIRequestService {
		this.config.paginationStrategy = strategy;
		return this;
	}

	paginationStrategySet (): boolean {
		return !!this.config.paginationStrategy;
	}

	getPaginationStrategy (): PaginationStrategy {
		return this.config.paginationStrategy;
	}

	setPaginationOptions (options: PaginationOptions): SDAngularJsonAPIRequestService {
		this.config.paginationOptions = options;
		return this;
	}

	paginationOptionsSet (): boolean {
		return !!this.config.paginationOptions;
	}

	getPaginationOptions (): PaginationOptions {
		return this.config.paginationOptions;
	}

	registerResource (name: any, instance: any): SDAngularJsonAPIRequestService {
		name = pluralize(name).toLowerCase();
		if ( !find(this.resources, { type: name }) ) {
			this.resources.push({ type: name, instance: instance });
		}
		return this;
	}

	getResourceInstance (name: string): any | undefined { // TODO add proper type return
		return find(this.resources, { type: name });
	}

	send (resource: string, data: any = {}, appendToUrl: string | string[] = [],
	      requestType: string = 'get'): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		if ( !this.config.baseUrl || this.config.baseUrl.length <= 0 ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_BASE_URL_SET);
		}
		return new Observable((observer) => {
			this.http.request<Collection | JSONAPIErrorResponse[] | HttpErrorResponse>(
				requestType, this.constructUrl(resource, appendToUrl), { body: data }).subscribe(
				(success: Collection) => {
					observer.next(success);
				},
				(errorResponse: HttpErrorResponse) => {
					if ( errorResponse.error && errorResponse.error.errors ) {
						observer.error(new JSONAPIErrorBag(errorResponse.error.errors));
					} else {
						observer.error(errorResponse);
					}
				}
			);
		});
	}

	sendWithUrl (url, data: any = {},
	             requestType: string = 'get'): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		return new Observable((observer) => {
			this.http.request<Collection | JSONAPIErrorResponse[] | HttpErrorResponse>(
				requestType, url, { body: data }).subscribe(
				(success: Collection) => {
					observer.next(success);
				},
				(errorResponse: HttpErrorResponse) => {
					if ( errorResponse.error && errorResponse.error.errors ) {
						observer.error(new JSONAPIErrorBag(errorResponse.error.errors));
					} else {
						observer.error(errorResponse);
					}
				}
			);
		});
	}

	private constructUrl (resource: string, appendToUrl: string | string[] = []): string {
		let baseUrl = this.config.baseUrl;
		let url = '';
		if ( baseUrl.charAt(baseUrl.length - 1) === '/' ) {
			baseUrl = baseUrl.slice(0, baseUrl.length - 1);
		}
		if ( this.config.version && this.config.version.length > 0 ) {
			const version = removeSlashes(this.config.version);
			url = `${baseUrl}/${version}/${resource}`;
		} else {
			url = `${baseUrl}/${resource}`;
		}
		if ( appendToUrl && appendToUrl.length > 0 ) {
			if ( typeof appendToUrl === 'string' ) {
				if ( appendToUrl.charAt(0) === '?' ) {
					url += removeSlashes(appendToUrl);
				} else {
					url += '/' + removeSlashes(appendToUrl);
				}
			} else if ( Array.isArray(appendToUrl) ) {
				url += '/' + each(appendToUrl, (el: string) => {
					return removeSlashes(el);
				}).join('/');
				url = url.replace(/\/\?/gmi, '?');
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.INVALID_URL_APPENDIX);
			}
		}
		return url;
	}
}
